import { useId } from 'react';
import { Link } from 'react-router-dom';
import { RepeatIcon, CheckIcon, ArrowRightIcon } from 'lucide-react';
import { usePages } from '../../contexts/SiteContext';
import { useFeatures } from '../../hooks/useFeatures';
import { quietLink, sectionHeading } from '../../ui/sections';
import { btnOnClay } from '../../ui/styles';
type Props = {
  /**
   * `full`: het hele verhaal met alle voordelen, op klei: het ene diepe vlak van de webshop.
   * `short`: dezelfde woorden zonder de voordelenlijst, licht (shell), voor pagina's die hun klei
   * al elders gebruiken (Protocollen). Nooit anders geformuleerd: alles komt uit Paginateksten › Abonnement.
   */
  size?: 'full' | 'short';
};
const CTA = 'Stel je abonnement samen';

/** Het abonnement: één bron voor elke pagina. Op klei alleen `text-shell` (5,6:1); blush alleen voor iconen. */
export function SubscriptionBlock({
  size = 'full'
}: Props) {
  const {
    subscription
  } = usePages();
  const headingId = useId();
  // Abonnement uitgezet (Instellingen › Koppelingen & privacy › Onderdelen): het blok staat nergens.
  const {
    subscriptions
  } = useFeatures();
  if (!subscriptions) return null;
  if (size === 'short') {
    return <section className="bg-shell py-16 sm:py-20" aria-labelledby={headingId} data-id="element-8816" data-mp="5ndgg9yn4oox">
        <div className="mx-auto grid max-w-content gap-6 px-6 lg:grid-cols-12 lg:items-end lg:gap-16 lg:px-10" data-id="element-8817" data-mp="9op6gglffbxn">
          <div className="lg:col-span-8" data-id="element-8818" data-mp="xejjoz9nr2if">
            <p className="flex items-center gap-2 text-[14px] text-clay" data-id="element-8819" data-mp="xfkx3ae5c13b">
              <RepeatIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8820" data-mp="12dmdtoo3nobm" />
              {subscription.name}
            </p>
            <h2 id={headingId} className="mt-4 type-sub text-ink" data-id="element-8821" data-mp="olg5upv3onn9">
              {subscription.claim}
            </h2>
            <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-muted" data-id="element-8822" data-mp="slgokmp8fl04">{subscription.intro}</p>
            <p className="mt-3 text-[14px] text-muted" data-id="element-8823" data-mp="1gbavknmvefcg">
              {subscription.price} · {subscription.smallPrint}
            </p>
          </div>
          <div className="lg:col-span-4 lg:flex lg:justify-end" data-id="element-8824" data-mp="1uyt6i3r1067b">
            <Link to="/webshop" className={`${quietLink} text-ink`} data-id="element-8825" data-mp="1qyiotttvfrcm">
              {CTA}
              <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8826" data-mp="1lz6176v6eg8z" />
            </Link>
          </div>
        </div>
      </section>;
  }
  return <section className="bg-clay py-20 text-shell sm:py-24 lg:py-32" aria-labelledby={headingId} data-id="element-8827" data-mp="1gzti8x7fdtbo">
      <div className="mx-auto grid max-w-content gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8828" data-mp="1c29t3296vw4y">
        <div className="lg:col-span-5" data-id="element-8829" data-mp="178yvlrq9orpp">
          <p className="flex items-center gap-2 text-[14px] text-shell" data-id="element-8830" data-mp="138evcfaed7zc">
            <RepeatIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8831" data-mp="136ak1ca656p1" />
            {subscription.name}
          </p>
          <h2 id={headingId} className={`mt-5 ${sectionHeading}`} data-id="element-8832" data-mp="tb0h8mby4va7">
            {subscription.claim}
          </h2>
          <p className="mt-6 max-w-md text-[17px] leading-relaxed text-shell" data-id="element-8833" data-mp="ob4d8n3913un">{subscription.intro}</p>
          <p className="mt-5 text-[14px] text-shell" data-id="element-8834" data-mp="e88vftbalntg">
            {subscription.price} · {subscription.smallPrint}
          </p>
          <Link to="/webshop" className={`mt-9 ${btnOnClay}`} data-id="element-8835" data-mp="j4zacdbd2dda">
            {CTA}
          </Link>
        </div>

        <ul className="grid self-center border-t border-shell/30 sm:grid-cols-2 lg:col-span-7" data-id="element-8836" data-mp="o3k886354kyk">
          {subscription.perks.map(perk => <li key={perk.title} className="border-b border-shell/30 py-7 sm:pr-8" data-id="element-8837" data-mp="3rmvljphsi65e">
              <h3 className="flex items-start gap-2.5 type-name font-light text-shell" data-id="element-8838" data-mp="fsvx2hlo7xa">
                <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-blush" strokeWidth={2} aria-hidden="true" data-id="element-8839" data-mp="5f5kpss17p2i" />
                {perk.title}
              </h3>
              <p className="mt-2 pl-[1.625rem] text-[15px] leading-relaxed text-shell" data-id="element-8840" data-mp="a7f4er2jzswc">{perk.body}</p>
            </li>)}
        </ul>
      </div>
    </section>;
}